/**
 * Klient wspólnych tras plików pól typu „plik” (ADR-0114).
 *
 *  - `uploadFieldFile` → `POST /api/field-files` (plik roboczy; do rekordu przypina go dopiero
 *    zapis rekordu — wartością pola jest zwrócone `id`),
 *  - `downloadFieldFile` → `GET /api/field-files/:id/content` (strumień przez serwer, zapis na
 *    dysk pod oryginalną nazwą).
 */

import { api } from '@/lib/api';

/** Metadane pliku pokazywane obok wartości pola (nazwa zamiast identyfikatora). */
export interface FieldFileMeta {
  fileName: string;
  mimeType: string;
  sizeBytes: number;
}

export interface UploadedFieldFile extends FieldFileMeta {
  id: string;
}

/** Gdzie żyje pole — dziś kolumna tabeli dodatkowej. */
export interface FieldFileTarget {
  tableId: string;
  fieldSlug: string;
  /** Rekord-rodzic, gdy już istnieje (edycja); `null` przy tworzeniu. */
  parentId: string | null;
}

/** Maksymalny rozmiar pliku — lustro limitu serwera (jak załączniki). */
export const FIELD_FILE_MAX_BYTES = 50 * 1024 * 1024;

export async function uploadFieldFile(
  target: FieldFileTarget,
  file: File,
): Promise<UploadedFieldFile> {
  const formData = new FormData();
  formData.append('target', 'additional');
  formData.append('tableId', target.tableId);
  formData.append('fieldSlug', target.fieldSlug);
  if (target.parentId) formData.append('parentId', target.parentId);
  formData.append('file', file);
  const res = await api.post<{ file: UploadedFieldFile }>('/field-files', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data.file;
}

export async function downloadFieldFile(fileId: string, fileName: string): Promise<void> {
  const res = await api.get<Blob>(`/field-files/${fileId}/content`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

/** Kod błędu wysyłania z odpowiedzi serwera (`FILE_TOO_LARGE`, `MIME_TYPE_NOT_ALLOWED`…). */
export function fieldFileUploadErrorCode(err: unknown): string | null {
  const data = (err as { response?: { data?: { error?: unknown } } }).response?.data;
  return typeof data?.error === 'string' ? data.error : null;
}
